import React, { useCallback } from 'react';
import { useForm } from 'react-final-form';
import { TextField } from 'components/Forms/TextField';
import { TextAreaField } from 'components/Forms/TextAreaField';
import { useWindowSize } from 'hooks/useWindowSize';
import { useFieldValue } from 'hooks/useFormHelpers';

interface RecipientProps {
  selectedRecipient: { value: string; label: string };
  handleRowClick: (rowId: string) => void;
}

export const RecipientDetails = React.memo(function RecipientDetails({
  selectedRecipient,
  handleRowClick,
}: RecipientProps) {
  const form = useForm();
  const { width } = useWindowSize();
  const isMobile = width < 768;
  const recipientDetails = useFieldValue('recipient_details');

  const recData = recipientDetails?.[selectedRecipient.value];

  const handleAddSection = useCallback(() => {
    const currentSections = recData?.sections || [];
    const newSections = [...currentSections, { label: '', value: '' }];

    form.change(`recipient_details.${selectedRecipient.value}.sections`, newSections);
  }, [recData?.sections, form, selectedRecipient.value]);

  const handleDeleteSection = useCallback(() => {
    const currentSections = recData?.sections || [];
    const selectedSectionIndex = currentSections.length - 1;
    const updatedSections = currentSections.filter((_, index) => index !== selectedSectionIndex);

    form.change(`recipient_details.${selectedRecipient.value}.sections`, updatedSections);
  }, [recData?.sections, form, selectedRecipient.value]);

  return (
    <div className="grid full-width">
      <div className="divider" />
      <div className="details-content">
        <span className="oath-utility gray">
          Add additional details for recipient below. All fields are optional.
        </span>
        <TextField
          name={`recipient_details.${selectedRecipient.value}.header_1`}
          type="text"
          placeholder="Header 1 (140 characters max)"
          className="gray-input"
          maxLength={140}
        />
        <TextField
          name={`recipient_details.${selectedRecipient.value}.header_2`}
          type="text"
          placeholder="Header 2 (140 characters max)"
          className="gray-input"
          maxLength={140}
        />
        <TextAreaField
          label="Recipient Summary (recommended 500 characters)"
          name={`recipient_details.${selectedRecipient.value}.summary`}
          placeholder="Recommended 500 characters max"
          className="gray-input"
          maxLength={500}
        />
        {recData?.sections?.length > 0 && (
          <div className="section-titles">
            <div className="col-md-4 section-column">
              <span className="oath-utility gray">Sections (45 characters max)</span>
            </div>
            <div className="col-md-8 section-column">
              <span className="oath-utility gray">
                Description (recommended 300 characters max)
              </span>
            </div>
          </div>
        )}
        {recData?.sections?.map((_, index) => (
          <div className="row" key={index}>
            <div className="col-md-4 section-column">
              <TextField
                label={!isMobile ? '' : `Label ${index + 1} (45 characters max)`}
                name={`recipient_details.${selectedRecipient.value}.sections.${index}.label`}
                type="text"
                placeholder={`Label ${index + 1}`}
                className="gray-input"
                maxLength={45}
              />
            </div>
            <div className="col-md-8 section-column">
              <TextField
                label={!isMobile ? '' : `Description ${index + 1} (recommended 300 characters max)`}
                name={`recipient_details.${selectedRecipient.value}.sections.${index}.value`}
                type="text"
                placeholder={`Description ${index + 1}`}
                className="gray-input"
                maxLength={300}
              />
            </div>
          </div>
        ))}
        <div className="links-section-container">
          {recData?.sections?.length > 0 && (
            <button
              type="button"
              className="oath-bodysmall link-text gray"
              onClick={e => {
                e.stopPropagation();
                handleDeleteSection();
              }}
            >
              Delete Section
            </button>
          )}
          <button
            type="button"
            className="oath-bodysmall link-text gray add-section-link"
            onClick={e => {
              e.stopPropagation();
              handleAddSection();
            }}
          >
            Add Section
          </button>
        </div>
        {isMobile && (
          <div className="close-details-mobile">
            <button
              type="button"
              className="oath-bodysmall link-text oathBlue"
              onClick={e => {
                e.stopPropagation();
                handleRowClick(selectedRecipient.value);
              }}
            >
              Close Details
            </button>
          </div>
        )}
      </div>
    </div>
  );
});
